import { useEffect, useRef, useState } from "react";

import { watch, type Reception } from "./whep";

/**
 * What a listener sees when the host is broadcasting from the studio.
 *
 * ## Why this is not the dock
 *
 * In catalogue mode the room tells the dock which track to play and the dock
 * fetches it from the CDN, which is why a broadcast survives navigating away.
 * In studio mode there is no track to fetch: the audio the host mixed and the
 * picture they composited arrive together as one WebRTC stream, and it has a
 * picture, which the dock has nowhere to put.
 *
 * So this element owns playback for the duration, and `RoomAudio` stands down -
 * see the note there. Two players fed from the same room would mean hearing the
 * track twice, a second or so apart.
 *
 * ## Muted and playing, with one button to join
 *
 * Browsers refuse to autoplay audible media, and a video that silently fails to
 * start looks like a broken stream rather than a policy. So it starts muted and
 * playing - which *is* allowed, and which proves the stream is live before
 * anybody commits to it.
 *
 * Turning that into sound used to be two controls in two places: "Listen live"
 * beside the video to join the room, and an Unmute button on it. They are one
 * intention. There is now a play button in the middle of the picture, where
 * every video on the internet keeps it, and pressing it does the whole thing -
 * unmutes, and puts the listener in the room so they are counted and can talk.
 *
 * Once they are listening the button gets out of the way and returns on hover
 * as pause, which is the same convention from the other direction.
 *
 * ## The stream can be live before it is playable
 *
 * There is a window after the host presses start where the room says stream
 * mode and the broadcast has not reached Cloudflare's edge yet, so subscribing
 * is refused. That is normal and brief, and it is why a failure here retries on
 * a backoff rather than reporting a fault at somebody who has done nothing
 * wrong.
 *
 * What it does not do is retry forever. That turns a permanent problem into a
 * permanent spinner, which is indistinguishable from a host who has not started
 * yet and impossible to report usefully.
 *
 * ## No seeking, no cushion, no numbers to tune
 *
 * All of that went with HLS - see `whep.ts`. There is no buffer target here
 * because there is no buffer to target: frames arrive as the host produces
 * them, and the live edge is the only position that exists.
 */

/** How many times to try connecting before saying so. */
const ATTEMPTS = 8;

/**
 * Backoff between attempts, in milliseconds.
 *
 * Front-loaded and capped rather than doubling indefinitely. The common case is
 * a listener who opened the profile a second before the host's first frame
 * landed, and they should be watching almost immediately rather than serving a
 * sentence from an exponent.
 */
const BACKOFF = [500, 1000, 2000, 3000, 4000, 5000, 5000, 5000];

export function StreamPlayer({
  url,
  listening,
  onJoin,
  onLeave,
}: {
  url: string;
  /** Whether the viewer is in the room, which the play button toggles. */
  listening: boolean;
  onJoin: () => void;
  onLeave: () => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [paused, setPaused] = useState(false);
  const [waiting, setWaiting] = useState(true);
  /*
   * What went wrong, in the transport's own words.
   *
   * A listener does not know what it means; the host they report it to can read
   * it back, and that is the point. The alternative - which this replaced - was
   * a "connecting..." that never finished and could not be diagnosed without
   * attaching a debugger to somebody else's browser.
   */
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    const video = ref.current;

    if (!video) return;

    setWaiting(true);
    setFailed(null);

    /*
     * The effect can be torn down mid-connect - a re-render, a URL change, the
     * listener navigating away - and `watch` takes seconds to resolve. Without
     * this the late arrival attaches a stream to an element the component no
     * longer owns, and leaves a peer connection running with nothing left to
     * close it.
     */
    let live = true;
    let session: Reception | null = null;
    let timer: number | undefined;

    const attempt = async (n: number) => {
      try {
        const reception = await watch(url);

        if (!live) {
          void reception.stop();

          return;
        }

        session = reception;
        video.srcObject = reception.stream;
        setWaiting(false);

        /*
         * A broadcast that drops is not a fault to report - the host's
         * connection wobbled, or they stopped and restarted - so it goes back
         * through the same retry path from the top. `closed` is excluded
         * deliberately: that state is us closing it.
         */
        reception.onStateChange((state) => {
          if (!live || (state !== "failed" && state !== "disconnected")) return;

          session = null;
          setWaiting(true);
          void attempt(0);
        });
      } catch (cause) {
        if (!live) return;

        if (n + 1 >= ATTEMPTS) {
          setFailed(cause instanceof Error ? cause.message : "unknown error");

          return;
        }

        timer = window.setTimeout(() => void attempt(n + 1), BACKOFF[n]);
      }
    };

    void attempt(0);

    return () => {
      live = false;
      clearTimeout(timer);
      video.srcObject = null;
      void session?.stop();
    };
  }, [url]);

  useEffect(() => {
    const video = ref.current;

    if (!video) return;

    video.muted = muted;
    video.volume = volume;

    // Not while deliberately paused, or changing the volume would restart a
    // broadcast somebody stopped.
    if (paused) return;

    void video.play().catch(() => {
      // Autoplay refused even muted, which some configurations do. The centre
      // play button is what the listener sees, and their click starts it.
    });
  }, [muted, volume, paused]);

  /** Start listening properly, from a real click. */
  const join = () => {
    setMuted(false);
    setPaused(false);

    void ref.current?.play().catch(() => {});

    if (!listening) onJoin();
  };

  const leave = () => {
    setPaused(true);
    ref.current?.pause();

    if (listening) onLeave();
  };

  /*
   * Listening means all three: in the room, audible, and running. Anything less
   * is a state the play button should be offering to fix.
   */
  const active = listening && !muted && !paused;

  return (
    <div
      className="group relative w-full overflow-hidden rounded-[20px] border border-[#26264e] bg-black"
      data-stream-player
    >
      <video
        ref={ref}
        // `playsInline` or iOS takes the stream fullscreen the moment it plays,
        // which throws the listener out of the page they were reading.
        playsInline
        autoPlay
        muted
        className="block aspect-video w-full"
        data-stream-video
        onPlaying={() => {
          setWaiting(false);
          setPaused(false);
        }}
        onWaiting={() => setWaiting(true)}
        // Chrome pauses media for reasons of its own - a device change, a
        // policy - and the button has to tell the truth about it.
        onPause={() => setPaused(true)}
      />

      {/*
        The one control that matters, in the middle of the picture.

        Visible whenever the listener is not fully in - muted, paused, or not in
        the room - and on hover once they are, so pausing is where they will
        reach for it. Hidden the rest of the time, because a permanent button
        over a broadcast is a permanent thing in the way of it.
      */}
      {!failed && !waiting && (
        <button
          type="button"
          onClick={active ? leave : join}
          aria-label={active ? "Pause" : "Listen"}
          data-stream-toggle
          className={`absolute inset-0 flex items-center justify-center transition-opacity ${
            active
              ? "opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
              : "opacity-100"
          }`}
        >
          <span
            className={`flex h-20 w-20 items-center justify-center rounded-full backdrop-blur-sm transition-transform hover:scale-105 ${
              active ? "bg-black/55" : "bg-ink/95"
            }`}
          >
            {active ? (
              <svg
                viewBox="0 0 24 24"
                className="h-8 w-8 fill-white"
                aria-hidden
              >
                <rect x="6" y="4.5" width="4" height="15" rx="1" />
                <rect x="14" y="4.5" width="4" height="15" rx="1" />
              </svg>
            ) : (
              // Nudged right: a triangle centred on its bounding box reads as
              // sitting left of centre inside a circle.
              <svg
                viewBox="0 0 24 24"
                className="ml-1 h-9 w-9 fill-app"
                aria-hidden
              >
                <path d="M7 4.5v15l13-7.5z" />
              </svg>
            )}
          </span>
        </button>
      )}

      {failed ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/75 px-6 text-center">
          <span className="meta-caps text-hot-text">
            the stream could not be played
          </span>
          <span className="font-mono text-[10px] text-ghost" data-stream-error>
            {failed}
          </span>
        </div>
      ) : (
        waiting && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <span className="meta-caps text-ghost">connecting…</span>
          </div>
        )
      )}

      {/*
        Volume, and only once they are in.

        Before joining, the play button in the middle is the single thing to
        press. An Unmute here alongside it would be a second way to ask for
        sound that skipped putting the listener in the room - so they would be
        hearing a broadcast while its listener count and chat had no idea they
        were there.
      */}
      {!failed && listening && (
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8">
          <button
            type="button"
            onClick={() => setMuted((on) => !on)}
            aria-pressed={!muted}
            aria-label={muted ? "Unmute" : "Mute"}
            data-stream-unmute
            className="shrink-0 rounded-[var(--radius-pill)] bg-ink px-4 py-1.5 text-[13px] font-bold text-app transition-opacity hover:opacity-90"
          >
            {muted ? "Unmute" : "Mute"}
          </button>

          <label className="flex flex-1 items-center gap-2">
            <span className="sr-only">Volume</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={muted ? 0 : volume}
              data-stream-volume
              onChange={(e) => {
                const next = Number(e.target.value);

                setVolume(next);
                // Dragging the slider up is how people unmute, whatever the
                // button says.
                if (next > 0) setMuted(false);
              }}
              className="w-full max-w-[160px] accent-[var(--color-ink)]"
            />
          </label>

          <span className="meta-caps shrink-0 text-white/70">live</span>
        </div>
      )}
    </div>
  );
}
